import {
  type MarketButton, MarketButtonGroup, MarketHeader, type MarketModalFull, MarketModalPartial,
} from '@market/react';
import type { ReactElement, ReactNode } from 'react';
import classNames from 'classnames';
import styles from './BankingModalBase.module.css';

/**
 * Base modal component that provides shared functionality for Banking modals.
 * This component handles the common structure and styling, while allowing
 * different modal wrapper components (MarketModalPartial vs MarketModalFull).
 *
 * This is an internal component - use BankingModalPartial or BankingModalFull instead.
 */

type Content = string | ReactNode;

interface BankingModalBaseProps {
  readonly active: boolean;
  readonly buttons?: ReactElement<typeof MarketButton>[];
  readonly content: Content;
  readonly title: string;
  readonly onModalClose: () => void;
  readonly ModalWrapper: typeof MarketModalFull | typeof MarketModalPartial;
}

export const BankingModalBase = ({
  active, buttons, content, title, onModalClose, ModalWrapper,
}: BankingModalBaseProps) => {
  // Use stack alignment for partial modals, left alignment for full modals
  const buttonAlignment = ModalWrapper === MarketModalPartial ? 'stack' : 'left';

  return active
    ? <ModalWrapper>
      <MarketHeader className={styles.header} data-testid="header" onMarketHeaderNavigate={onModalClose}>
        <h2>{title}</h2>
      </MarketHeader>
      <section className="main">
        {typeof content === 'string' ?
          <p className={classNames('paragraph-30', styles.description)}>{content}</p>
          : content}
        {buttons && buttons.length > 0 && (
          <MarketButtonGroup className={styles.buttons} alignment={buttonAlignment}>
            {buttons.map((button) => button)}
          </MarketButtonGroup>
        )}
      </section>
    </ModalWrapper>
    : null;
};
